import logger from '../../../../utils/logger';
/**
 * Service for deleting specific negotiation entries
 * Handles deletion of individual offers within negotiation rounds
 */

import axiosInstance from '../../../../fetchConfig/axiosConfig.js';
import { getMostRecentOfferValue } from '../../../../utils/mostRecentOfferUtils.js';
import { usePersistedDataStore } from '../../../store';

const offerIdsMatch = (a, b) => String(a) === String(b);

const offerIdInDeleteSet = (offerId, idsToDelete) =>
  idsToDelete.some((id) => offerIdsMatch(id, offerId));

/**
 * Delete one or more negotiation entries by OfferID(s)
 * Unified function that handles both single and batch deletion
 * 
 * @param {Object} options - Options for deletion
 * @param {string|Array} options.offerId - Single OfferID (for backward compatibility)
 * @param {Array} options.offerIds - Array of OfferIDs for batch deletion
 * @param {string} options.parcelId - The ParcelID of the case (optional for batch deletion)
 * @param {number} options.taxYear - The tax year
 * 
 * @returns {Promise<Object>} Result of the deletion operation
 */
export const deleteNegotiationEntry = async (options) => {
  const {
    offerId,
    offerIds,
    parcelId,
    taxYear
  } = options;

  // Get store functions directly
  const getNegotiationObj = usePersistedDataStore.getState().negotiationObj;
  const setNegotiationObj = usePersistedDataStore.getState().setNegotiationObj;
  const setNegotiationObjVersion = usePersistedDataStore.getState().setNegotiationObjVersion;

  // Normalize to array: if offerId is provided, use it; otherwise use offerIds array
  const offerIdsToDelete = offerId ? [offerId] : (offerIds || []);
  
  if (!offerIdsToDelete || offerIdsToDelete.length === 0) {
    throw new Error('Either offerId or offerIds (array) is required for deletion.');
  }

  if (!taxYear) {
    throw new Error('taxYear is required for deletion.');
  }

  if (!getNegotiationObj || !setNegotiationObj || !setNegotiationObjVersion) {
    throw new Error('Negotiation store is not available.');
  }

  // Update local negotiation object state immediately (optimistic update)
  // If parcelId is provided, update only that case; otherwise update all affected cases
  const casesToUpdate = parcelId 
    ? [getNegotiationObj.cases.find(c => c.parcel_id === parcelId)].filter(Boolean)
    : getNegotiationObj.cases.filter(c => 
        c.Negotiations && c.Negotiations.some(round => 
          round.offers && round.offers.some(offer => 
            offerIdInDeleteSet(offer.OfferID, offerIdsToDelete)
          )
        )
      );
  
  if (casesToUpdate.length > 0) {
    let updatedNegotiationObj = JSON.parse(JSON.stringify(getNegotiationObj));
    
    casesToUpdate.forEach(caseToUpdate => {
      const caseIndex = updatedNegotiationObj.cases.findIndex(c => c.parcel_id === caseToUpdate.parcel_id);
      if (caseIndex !== -1) {
        const updatedNegotiations = removeOffersFromNegotiations(
          updatedNegotiationObj.cases[caseIndex].Negotiations,
          offerIdsToDelete
        );

        updatedNegotiationObj.cases[caseIndex] = {
          ...updatedNegotiationObj.cases[caseIndex],
          Negotiations: updatedNegotiations,
          OfferValue: getMostRecentOfferValue(updatedNegotiations, 'Aventine'),
          TownOffer: getMostRecentOfferValue(updatedNegotiations, 'Town'),
        };
      }
    });
    
    setNegotiationObj(updatedNegotiationObj);
    setNegotiationObjVersion(v => v + 1);
  }

  // Call backend API asynchronously without blocking
  axiosInstance.post('/delete_offers', {
    offerIds: offerIdsToDelete,
    taxYear: taxYear
  }).then(response => {
    if (!response.data.success) {
      logger.error('Backend deletion failed:', response.data.error || 'Failed to delete negotiation entry/entries');
    } else {
      logger.log(`Negotiation entry/entries deleted successfully: ${response.data.deleted_count} deleted`);
    }
  }).catch(error => {
    logger.error('Error deleting negotiation entry/entries on backend:', error);
  });
  
  return {
    success: true,
    message: `Successfully deleted ${offerIdsToDelete.length} negotiation entry/entries`,
    deleted_count: offerIdsToDelete.length,
    offerIds: offerIdsToDelete,
    parcelId: parcelId
  };
};

/**
 * Remove one or more offers from the negotiations array
 * 
 * @param {Array} negotiations - The negotiations array
 * @param {string|Array} offerIds - Single OfferID or array of OfferIDs to remove
 * @returns {Array} Updated negotiations array
 */
const removeOffersFromNegotiations = (negotiations, offerIds) => {
  if (!negotiations || !Array.isArray(negotiations)) {
    return [];
  }

  // Normalize to array
  const offerIdsToRemove = Array.isArray(offerIds) ? offerIds : [offerIds];
  
  const updatedNegotiations = [];
  
  for (const round of negotiations) {
    if (round.offers && Array.isArray(round.offers)) {
      // Filter out offers with matching OfferIDs
      const updatedOffers = round.offers.filter(offer => {
        return !offerIdInDeleteSet(offer.OfferID, offerIdsToRemove);
      });
      
      // Only include the round if it still has offers
      if (updatedOffers.length > 0) {
        updatedNegotiations.push({
          ...round,
          offers: updatedOffers
        });
      }
    } else {
      // Keep rounds without offers as-is
      updatedNegotiations.push(round);
    }
  }
  
  return updatedNegotiations;
};

/**
 * Get the OfferID for a specific offer in a round
 * 
 * @param {Array} negotiations - The negotiations array
 * @param {number} roundNumber - The round number
 * @param {string} offeredBy - The party who made the offer ('Aventine' or 'Town')
 * @returns {string|null} The OfferID or null if not found
 */
export const getOfferId = (negotiations, roundNumber, offeredBy) => {
  if (!negotiations || !Array.isArray(negotiations)) {
    return null;
  }

  const round = negotiations.find(r => r.round === roundNumber);
  if (!round || !round.offers || !Array.isArray(round.offers)) {
    return null;
  }

  const offer = round.offers.find(o => o.OfferedBy === offeredBy);
  return offer ? offer.OfferID ?? null : null;
};
